import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { api } from './api';

const AuthCtx = createContext(null);

// Wave 3.99l — Legacy → new alias map. Mirrors ``LEGACY_ALIASES``
// in ``backend/routes/users.py`` and ``_RBAC_LEGACY_ALIASES`` in
// ``backend/core/auth_deps.py``. Keep in sync when either side
// gains a new alias.
const LEGACY_ALIASES = {
  'masters.view': ['vendors.view', 'goldsmiths.view'],
  'masters.manage': ['vendors.manage', 'goldsmiths.manage'],
  // Wave 4.10 — Labour ledger write implies read.
  'goldsmiths.amount_ledger.manage': ['goldsmiths.amount_ledger.view'],
  'diamonds.view': ['diamonds.packets.view', 'materials.diamonds.see'],
  'diamonds.manage': [
    'diamonds.classifications.manage',
    'diamonds.packets.manage', 'diamonds.packets.receive', 'diamonds.packets.issue',
    'materials.diamonds.edit_requirement', 'materials.diamonds.record_purchase', 'materials.diamonds.issue',
  ],
  'diamonds.packets.view':   ['materials.diamonds.see'],
  'diamonds.packets.manage': ['materials.diamonds.edit_requirement', 'materials.diamonds.record_purchase'],
  'diamonds.packets.receive':['materials.diamonds.record_purchase'],
  'diamonds.packets.issue':  ['materials.diamonds.issue'],
  'color_stones.view': ['materials.color_stones.see'],
  'pearls.view':       ['materials.pearls.see'],
  'gold.view':         ['materials.gold.see'],
  'gold.manage': [
    'gold.manage_rates', 'gold.manage_purities', 'gold.ledger_entry',
    'materials.gold.record_purchase', 'materials.gold.edit_requirement',
  ],
  'gold.manage_rates': ['materials.gold.record_purchase'],
  'gold.ledger_entry': ['materials.gold.record_purchase'],
  'orders.manage': ['orders.create', 'orders.edit', 'orders.complete', 'orders.issue_to_goldsmith'],
  'orders.issue_to_goldsmith': [
    'materials.gold.issue', 'materials.diamonds.issue',
    'materials.color_stones.issue', 'materials.pearls.issue',
  ],
  'orders.edit': [
    'orders.edit_header',
    'materials.gold.edit_requirement', 'materials.diamonds.edit_requirement',
    'materials.color_stones.edit_requirement', 'materials.pearls.edit_requirement',
  ],
  'orders.create': [
    'materials.gold.edit_requirement', 'materials.diamonds.edit_requirement',
    'materials.color_stones.edit_requirement', 'materials.pearls.edit_requirement',
  ],
  // Wave 3.99l — Legacy master ``reports.view`` implicitly grants
  // access to every report tile (View + Value + Print) so existing
  // users don't lose any dashboards on redeploy.
  'reports.view': [
    'reports.unified_closing.view', 'reports.unified_closing.value', 'reports.unified_closing.print',
    'reports.memos.view', 'reports.memos.value', 'reports.memos.print',
    'reports.diamond_closing.view', 'reports.diamond_closing.value', 'reports.diamond_closing.print',
    'reports.color_stone_closing.view', 'reports.color_stone_closing.value', 'reports.color_stone_closing.print',
    'reports.pearl_closing.view', 'reports.pearl_closing.value', 'reports.pearl_closing.print',
    'reports.gold_ledger.view', 'reports.gold_ledger.value', 'reports.gold_ledger.print',
    'reports.jewellery.view', 'reports.jewellery.value', 'reports.jewellery.print',
  ],
  'reports.export': [
    'reports.unified_closing.print',
    'reports.memos.print',
    'reports.diamond_closing.print',
    'reports.color_stone_closing.print',
    'reports.pearl_closing.print',
    'reports.gold_ledger.print',
    'reports.jewellery.print',
  ],
  // Wave 3.99l — Legacy ``values.view`` expands to the three granular
  // value contexts so existing users retain full visibility.
  'values.view': [
    'values.raw_material.view',
    'values.jewellery_embedded.view',
    'values.jewellery_total.view',
  ],
  // Wave 3.99m — Per-report See Value now implies the corresponding
  // Financial Visibility context. Single source of truth for admins:
  // toggling ``reports.jewellery.value`` grants both embedded-material
  // and jewellery-total visibility; each raw-material report value
  // grants raw-material rate/value visibility across the portal.
  'reports.unified_closing.value':     ['values.raw_material.view'],
  'reports.diamond_closing.value':     ['values.raw_material.view'],
  'reports.gold_ledger.value':         ['values.raw_material.view'],
  'reports.color_stone_closing.value': ['values.raw_material.view'],
  'reports.pearl_closing.value':       ['values.raw_material.view'],
  'reports.jewellery.value':           ['values.jewellery_embedded.view', 'values.jewellery_total.view'],
};

const MATERIAL_ALIASES = {
  gold: 'gold',
  diamond: 'diamonds', diamonds: 'diamonds',
  color_stone: 'color_stones', color_stones: 'color_stones',
  colour_stone: 'color_stones', colour_stones: 'color_stones',
  pearl: 'pearls', pearls: 'pearls',
  // Wave 3.99z — Mountings / Semi-Finished Gold Pieces (chains, pech,
  // chaki, findings). Backend permission keys already exist as
  // ``materials.mountings.*``.
  mounting: 'mountings', mountings: 'mountings',
};

const ALL_MATERIALS = ['gold', 'diamonds', 'color_stones', 'pearls', 'mountings'];

// Wave 3.99z — Some materials use a different level suffix than the
// canonical 4-column matrix. Mountings uses ``edit_master`` instead of
// ``edit_requirement`` because mountings do not have "requirements"
// (they're master-list-managed). This map lets callers pass the
// canonical level and be transparently rewritten to the material's
// actual permission key. All other combinations pass through.
const MATERIAL_LEVEL_OVERRIDES = {
  mountings: {
    edit_requirement: 'edit_master',
  },
};

function resolveMaterialLevel(material, level) {
  const overrides = MATERIAL_LEVEL_OVERRIDES[material];
  if (overrides && overrides[level]) return overrides[level];
  return level;
}

function expandPermissions(perms) {
  const out = new Set(perms || []);
  // Iterate a few times so aliases-of-aliases resolve.
  for (let i = 0; i < 3; i++) {
    let added = false;
    for (const [legacy, expanded] of Object.entries(LEGACY_ALIASES)) {
      if (out.has(legacy)) {
        for (const k of expanded) {
          if (!out.has(k)) { out.add(k); added = true; }
        }
      }
    }
    if (!added) break;
  }
  return out;
}

function canonMaterial(m) {
  if (!m) return null;
  const key = String(m).trim().toLowerCase().replace(/[- ]/g, '_');
  return MATERIAL_ALIASES[key] || null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('inv_user') || 'null'); } catch { return null; }
  });
  const [activeCompanyId, setActiveCompanyId] = useState(() => localStorage.getItem('inv_active_company') || null);
  const [loading, setLoading] = useState(false);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      localStorage.setItem('inv_token', data.access_token);
      localStorage.setItem('inv_user', JSON.stringify(data.user));
      setUser(data.user);
      if (data.user.company_id) {
        localStorage.setItem('inv_active_company', data.user.company_id);
        setActiveCompanyId(data.user.company_id);
      }
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  // Unified portal login — one form for both the Inventique ERP and the Notes
  // module. The backend decides the destination from the email; we persist the
  // right token bundle and return the target so the caller can route.
  const portalLogin = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/portal-login', { email, password });
      if (data.target === 'notes') {
        localStorage.setItem('notes_token', data.access_token);
        localStorage.setItem('notes_user', JSON.stringify(data.user));
        if (data.user?.theme_pref) localStorage.setItem('notes_theme', data.user.theme_pref);
        return { target: 'notes', user: data.user };
      }
      localStorage.setItem('inv_token', data.access_token);
      localStorage.setItem('inv_user', JSON.stringify(data.user));
      setUser(data.user);
      if (data.user.company_id) {
        localStorage.setItem('inv_active_company', data.user.company_id);
        setActiveCompanyId(data.user.company_id);
      }
      return { target: 'inventique', user: data.user };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('inv_token');
    localStorage.removeItem('inv_user');
    localStorage.removeItem('inv_active_company');
    setUser(null);
    setActiveCompanyId(null);
    window.location.href = '/login';
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      localStorage.setItem('inv_user', JSON.stringify(data));
    } catch {}
  }, []);

  const switchCompany = useCallback((cid) => {
    setActiveCompanyId(cid);
    if (cid) localStorage.setItem('inv_active_company', cid);
    else localStorage.removeItem('inv_active_company');
  }, []);

  // Effective company_id used for API queries (super admin requires a selected company)
  const effectiveCompanyId = user?.role === 'super_admin' ? activeCompanyId : user?.company_id;

  // Wave 3.30 — Granular permission helper. Super Admin always returns true.
  // Wave 3.99h — Company Admin also bypasses.
  // Wave 3.99l — Legacy → new aliases are expanded so a user with
  // ``diamonds.packets.view`` naturally satisfies a ``materials.diamonds.see``
  // check (and vice-versa for the other legacy keys).
  const effectivePermissions = useMemo(() => {
    if (!user) return new Set();
    return expandPermissions(user.permissions || []);
  }, [user]);

  const hasPermission = useCallback((key) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if (user.role === 'company_admin') return true;
    return effectivePermissions.has(key);
  }, [user, effectivePermissions]);

  // Wave 3.99l — Material Access Matrix helper.
  // ``level`` ∈ ``'see' | 'edit_requirement' | 'record_purchase' | 'issue'``.
  // Wave 3.99z — Some materials remap a level (e.g. mountings uses
  // ``edit_master`` instead of ``edit_requirement``); the resolver
  // handles the translation transparently.
  // Super Admin + Company Admin bypass and get all materials at every
  // level.
  const hasMaterialAccess = useCallback((material, level = 'see') => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if (user.role === 'company_admin') return true;
    const canon = canonMaterial(material);
    if (!canon) return false;
    const resolved = resolveMaterialLevel(canon, level);
    return effectivePermissions.has(`materials.${canon}.${resolved}`);
  }, [user, effectivePermissions]);

  // Returns the array of materials the user is allowed to see at the given
  // level (defaults to 'see'). Useful for filtering lists / dashboards.
  const materialScope = useCallback((level = 'see') => {
    if (!user) return [];
    if (user.role === 'super_admin' || user.role === 'company_admin') return [...ALL_MATERIALS];
    return ALL_MATERIALS.filter((m) => {
      const resolved = resolveMaterialLevel(m, level);
      return effectivePermissions.has(`materials.${m}.${resolved}`);
    });
  }, [user, effectivePermissions]);

  // Wave 3.41 → 3.99l — Master switch was ``values.view``; now split
  // into three contexts. ``canViewValues`` remains for backward
  // compatibility (defaults to raw-material context, which is where
  // most callers look). New callers should prefer ``canViewValuesOf``.
  //
  // Contexts:
  //   'raw_material'         → rate + value across stock / on-approval / WIP
  //   'jewellery_embedded'   → material-at-cost inside a jewellery item
  //   'jewellery_total'      → labour + GST + finished-piece grand total
  const canViewValuesOf = useCallback((context = 'raw_material') => {
    if (!user) return false;
    if (user.role === 'super_admin' || user.role === 'company_admin') return true;
    const ctx = ['raw_material', 'jewellery_embedded', 'jewellery_total'].includes(context)
      ? context : 'raw_material';
    return effectivePermissions.has(`values.${ctx}.view`);
  }, [user, effectivePermissions]);
  // Legacy scalar helper. Kept as a boolean because many components use
  // it directly in JSX. Points at the raw-material context which is
  // what the current widgets reference (dashboard tiles, closing
  // balance rate/value columns, RML value column, etc.).
  const canViewValues = canViewValuesOf('raw_material');

  return (
    <AuthCtx.Provider value={{
      user, loading,
      login, portalLogin, logout, refreshMe,
      activeCompanyId, switchCompany, effectiveCompanyId,
      isSuperAdmin: user?.role === 'super_admin',
      isCompanyAdmin: user?.role === 'company_admin',
      // Customer-scoped, view-only Sales roles (Sales Person / Sales Manager).
      // They see ONLY active orders of their attributed customers; no exports,
      // no mutations (comments + @mentions are allowed by the backend).
      isSalesRole: user?.role === 'sales_person' || user?.role === 'sales_manager',
      hasPermission,
      hasMaterialAccess,
      materialScope,
      canViewValues,
      canViewValuesOf,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
